import {
  isAssetReadOnly,
  useCurrentAccount,
  WorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AddIcon,
  Button,
  CardButton,
  SlideAction,
  tokens,
  useSlideLayout,
} from '@gripp/shared-ui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAssetRouting } from '../assetRouteContext';
import { isActivityOrEditPage } from '../assetUtils';
import { AddAssetMediaType, AddFilesLinksSheet } from './addFilesLinksButton';
import { ViewAllSectionHeader } from './viewAllSectionHeader';

type AssetMediaFoldProps = {
  workspaceAsset: WorkspaceAsset | undefined;
  assetId: string;
  invalidateQuery: () => Promise<void>;
  forceShowing?: boolean;
};

export const AssetMediaFold: React.FC<AssetMediaFoldProps> = ({
  workspaceAsset,
  assetId,
  invalidateQuery,
  forceShowing,
}) => {
  const location = useLocation();
  const isOnAssetPage = location.pathname.startsWith('/assets/');
  const { t } = useTranslation();
  const mediaData = useMemo(
    () => workspaceAsset?.asset?.media || [],
    [workspaceAsset?.asset?.media]
  );
  const displayMediaData = useMemo(() => mediaData.slice(0, 3), [mediaData]);
  const { account } = useCurrentAccount();
  const isReadOnlyMode = isAssetReadOnly(account, workspaceAsset);
  const navigate = useNavigate();
  const slideLayout = useSlideLayout();
  const { onSlideChange } = slideLayout;
  // basePath and issueId come from the nearest AssetRouteProvider — set by every
  // component that renders AssetCard (both left and right columns).
  const { basePath, issueId } = useAssetRouting(assetId);
  const skipNextSlide = isActivityOrEditPage(location);
  const url = `${basePath}/files`;
  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<AddAssetMediaType | null>(
    null
  );
  const isSheetActive = isAddSheetOpen || !!selectedType;
  const asset = workspaceAsset?.asset;

  useEffect(() => {
    if (!slideLayout || !asset || isReadOnlyMode) return;
    if (!isSheetActive) {
      slideLayout.setColumnOverlay(null);
      return;
    }
    slideLayout.setColumnOverlay(
      <AddFilesLinksSheet
        asset={asset}
        invalidateQuery={invalidateQuery}
        isOpen={isAddSheetOpen}
        onClose={() => setIsAddSheetOpen(false)}
        selectedType={selectedType}
        onSelectedTypeChange={setSelectedType}
      />
    );
    return () => slideLayout.setColumnOverlay(null);
  }, [
    slideLayout,
    asset,
    isReadOnlyMode,
    invalidateQuery,
    isAddSheetOpen,
    isSheetActive,
    selectedType,
  ]);

  const handleFilePress = useCallback(
    async (fileId: string) => {
      if (!skipNextSlide) {
        await onSlideChange(SlideAction.NEXT);
      }

      navigate(`${url}/${fileId}`, {
        state: { source: 'assetCard' }
      });
    },
    [navigate, url, onSlideChange, skipNextSlide]
  );

  const handleViewAllFiles = useCallback(async () => {
    if (!skipNextSlide) {
      await onSlideChange(SlideAction.NEXT);
    }

    navigate(`${url}/all`);
  }, [navigate, url, onSlideChange, skipNextSlide]);

  if (!workspaceAsset || (!forceShowing && issueId && isOnAssetPage))
    return null;

  return (
    <View
      style={[
        styles.containerHeightCropCircle,
        !displayMediaData.length
          ? { height: 100 }
          : { height: displayMediaData.length * 60 + 32 },
      ]}
    >
      <ViewAllSectionHeader
        title={t('asset.files.title')}
        onPress={handleViewAllFiles}
      />
      {displayMediaData.length > 0 ? (
        <View style={[styles.listContainer]}>
          <FlatList
            data={displayMediaData}
            keyExtractor={(item, index) =>
              item.id?.toString() || index.toString()
            }
            scrollEnabled={false}
            renderItem={({ item }) => (
              <View style={styles.cardButtonContainer}>
                <CardButton
                  text={item.name}
                  color="white"
                  leftIcon="Document B"
                  rightIcon="Chevron Right"
                  showAvatars={false}
                  onPress={
                    item.id ? () => handleFilePress(item.id!) : undefined
                  }
                  bordered={false}
                />
              </View>
            )}
            showsVerticalScrollIndicator={true}
          />
        </View>
      ) : isReadOnlyMode ? null : (
        <Button
          style={styles.addFileEmpty}
          onPress={() => setIsAddSheetOpen(true)}
          leftIcon={<AddIcon />}
          variant={'rectangular'}
        >
          {t('asset.files.addFile')}
        </Button>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  containerHeightCropCircle: {
    maxHeight: 262,
    minHeight: 80,
    height: '100%',
    marginBottom: tokens.spacing.xl,
    paddingHorizontal: tokens.spacing.lg,
  },
  listContainer: {
    maxHeight: 184,
    overflow: 'hidden',
    marginBottom: 54,
  },
  addFileEmpty: {
    flex: 1,
    height: 56,
    maxHeight: 56,
    backgroundColor: tokens.colors.slate[100],
  },
  cardButtonContainer: {
    marginBottom: tokens.spacing.sm,
  },
});

